import type { DirectionRecord, SignalTrackRecordSummary } from '../../types'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'
import { formatPnlPct } from '../../utils/pnl'
import {
  MIN_WIN_RATE_SAMPLE,
  formatWinRateWithCount,
  hasWinRate,
  isSmallSample,
} from '../../utils/winRate'
import Tooltip from '../Tooltip'
import { IntervalNote, formatR } from './recordUI'

/**
 * Every short that has closed so far (all 10, 0 won) was opened Sep 15-17,
 * 2026 on correlated altcoins at a local low, just before BTC's +6% day on
 * Sep 18: one wrong market call, not ten independent tests (verified against
 * production and Kraken candles, 2026-09-27). The dated sentence is shown only
 * while that batch is the whole short record; the next short close retires it
 * automatically and the general note stands alone.
 */
const SHORT_BATCH_CLOSES = 10

export const SHORT_NOTE_GENERAL =
  'No edge has been demonstrated for short signals. FinCreator does not short with real money: Kraken spot trading cannot short.'
const SHORT_NOTE_BATCH =
  'Every short closed so far was opened Sep 15–17, 2026 on correlated altcoins just before a market-wide rally, so they behave like one wrong market call rather than independent results.'

type Tone = 'terminal' | 'light'

const TONE = {
  terminal: {
    card: 'rounded-lg border border-terminal-border bg-terminal-surface p-4',
    title: 'text-gray-100',
    label: 'text-gray-500',
    value: 'text-gray-100',
    muted: 'text-gray-500',
    note: 'text-gray-400',
    badge: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    divider: 'border-terminal-border',
    up: 'text-green-400',
    down: 'text-red-400',
  },
  light: {
    card: 'rounded-lg border border-gray-200 bg-white p-4 shadow-sm',
    title: 'text-gray-900',
    label: 'text-gray-500',
    value: 'text-gray-900',
    muted: 'text-gray-400',
    note: 'text-gray-600',
    badge: 'bg-amber-100 text-amber-800 ring-amber-300',
    divider: 'border-gray-200',
    up: 'text-success-600',
    down: 'text-danger-600',
  },
} as const

const SLICES = [
  { key: 'closed_all', title: 'All', testId: 'direction-all' },
  { key: 'closed_long', title: 'Longs', testId: 'direction-long' },
  { key: 'closed_short', title: 'Shorts', testId: 'direction-short' },
] as const

const signed = (value: number, tone: Tone) =>
  value > 0 ? TONE[tone].up : value < 0 ? TONE[tone].down : TONE[tone].value

/**
 * The engine's closed record grouped by direction: All, Longs and Shorts side
 * by side (stacked on a phone). Display only -- every number is the backend
 * summary's own, computed per slice with the headline's formulas
 * (services/signal_summary_service.py _direction_record); nothing is
 * recomputed here. Each rate travels with its W/closed count, is withheld
 * below MIN_WIN_RATE_SAMPLE like every rate in the app, and is flagged as a
 * small sample below SMALL_SAMPLE_SIZE.
 *
 * Every figure is gross, before fees, like every result a user sees: win
 * rate, W/L, avg win / avg loss and avg R (the summary's avg_r_gross). Avg R
 * is left out with ``showAvgR`` false -- the Dashboard's compact split.
 *
 * A creator's record (/c/<handle>) renders it too, with ``noun`` "calls" and
 * ``engine`` false: the dated Sep 15-17 note is about the engine's shorts and
 * never shows there; the general note (no real-money shorts) always does.
 */
const DirectionSplitPanel = ({
  summary,
  tone = 'terminal',
  showAvgR = true,
  noun = 'signals',
  engine = true,
}: {
  summary: Pick<SignalTrackRecordSummary, 'closed_all' | 'closed_long' | 'closed_short'>
  tone?: Tone
  showAvgR?: boolean
  noun?: 'signals' | 'calls'
  engine?: boolean
}) => {
  const t = TONE[tone]
  const shorts = summary.closed_short

  return (
    <section data-testid="direction-split" aria-labelledby="direction-split-title" className="space-y-3">
      <div>
        <h2 id="direction-split-title" className={`text-sm font-semibold ${t.title}`}>
          <Tooltip content={GLOSSARY.directionSplit}>Longs vs shorts</Tooltip>
        </h2>
        <p className={`text-xs ${t.note}`}>
          Closed {noun} grouped by direction. Same numbers, same formulas. Every figure is before fees.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {SLICES.map(({ key, title, testId }) => {
          const record: DirectionRecord | undefined = summary[key]
          if (!record) return null
          const quoted = hasWinRate(record.win_rate, record.closed)
          const small = isSmallSample(record.closed)
          return (
            <div key={key} className={t.card} data-testid={testId}>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-[11px] font-medium uppercase tracking-wider ${t.label}`}>{title}</p>
                {small && (
                  <Tooltip content={GLOSSARY.smallSample}>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ${t.badge}`}
                      data-testid={`${testId}-small-sample`}
                    >
                      small sample
                    </span>
                  </Tooltip>
                )}
              </div>

              <p
                className={`mt-1 font-mono text-xl font-bold tabular-nums ${quoted ? t.value : t.muted}`}
                data-testid={`${testId}-win-rate`}
              >
                {formatWinRateWithCount(record.win_rate, record.wins, record.closed)}
              </p>
              <p className={`text-xs ${t.note}`}>
                gross win rate · {record.wins}W/{record.losses}L
                {record.breakevens > 0 && `/${record.breakevens}BE`}
              </p>
              {!quoted && record.closed > 0 && (
                <p className={`text-xs ${t.note}`}>
                  {Math.max(MIN_WIN_RATE_SAMPLE - record.closed, 0)} more closes before a rate is quoted
                </p>
              )}
              {!quoted && record.closed === 0 && <p className={`text-xs ${t.note}`}>nothing closed yet</p>}
              {quoted && (
                <div className="text-xs">
                  <IntervalNote
                    interval={record.win_rate_interval}
                    format={(value) => `${value.toFixed(1)}%`}
                    testId={`${testId}-win-rate-interval`}
                  />
                </div>
              )}

              <dl className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className={t.label}>Avg win (gross)</dt>
                  <dd
                    className={`font-mono tabular-nums ${record.avg_win_pct == null ? t.muted : t.up}`}
                    data-testid={`${testId}-avg-win`}
                  >
                    {record.avg_win_pct == null ? '—' : formatPnlPct(record.avg_win_pct)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className={t.label}>Avg loss (gross)</dt>
                  <dd
                    className={`font-mono tabular-nums ${record.avg_loss_pct == null ? t.muted : t.down}`}
                    data-testid={`${testId}-avg-loss`}
                  >
                    {record.avg_loss_pct == null ? '—' : formatPnlPct(record.avg_loss_pct)}
                  </dd>
                </div>
                {showAvgR && (
                  <div className="flex justify-between gap-2">
                    <dt className={t.label}>Avg R (before fees)</dt>
                    <dd
                      className={`font-mono tabular-nums ${
                        record.avg_r_gross == null ? t.muted : signed(record.avg_r_gross, tone)
                      }`}
                      data-testid={`${testId}-avg-r`}
                    >
                      {record.avg_r_gross == null ? '—' : formatR(record.avg_r_gross)}
                    </dd>
                  </div>
                )}
              </dl>

              {key === 'closed_short' && (
                <div className={`mt-3 space-y-1 border-t pt-2 text-xs ${t.note} ${t.divider}`} data-testid="short-note">
                  {engine && shorts.closed > 0 && shorts.closed <= SHORT_BATCH_CLOSES && <p>{SHORT_NOTE_BATCH}</p>}
                  <p>{SHORT_NOTE_GENERAL}</p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default DirectionSplitPanel
